import {
  autocompletion,
  insertCompletionText,
  pickedCompletion,
  type Completion,
  type CompletionSource,
} from "@codemirror/autocomplete";
import { type Extension } from "@codemirror/state";
import { EditorView } from "@codemirror/view";

import { keys } from "@/hooks/queries";
import { api, queryClient, type GraphOverview, type SearchHit } from "@/lib/api";

/** `[[note name]]` autocomplete for the markdown editor.
 *
 *  The trigger is text-based rather than tree-based on purpose: while the user
 *  is still typing the target there is no closing `]]` yet, so the Lezer
 *  `WikiLink` node (see `markdown-extensions.ts`) has not formed and there is
 *  nothing in the tree to hang a completion off. Matching the raw text before
 *  the cursor is both simpler and correct mid-word. */

/** Header above the suggestion list. */
const LINK_SECTION = "Link to a note";

/** An OPEN wikilink ending at the cursor — `[[` plus the target typed so far.
 *  `[^[\]|\n]*` refuses to trigger past a `]` (the link is already closed), past
 *  a nested `[`, and past an alias `|` (the target has already been chosen).
 *  `CompletionContext.matchBefore` only ever looks at the current line, so the
 *  "no newline between" rule comes for free. */
const OPEN_WIKILINK = /\[\[[^[\]|\n]*/;

/** How many distinct query terms to remember before dropping the lot. Each entry
 *  is ≤10 tiny objects, so this only exists to stop a long writing session from
 *  retaining every prefix the user ever typed. */
const CACHE_LIMIT = 200;

/** How long a memoised term stays good. Long enough that backspacing over a
 *  prefix (or retyping one seconds later) costs nothing, short enough that a
 *  note created or renamed ELSEWHERE in the studio turns up in the picker on
 *  its own: without an expiry, a term that once matched nothing stayed empty
 *  for the life of the editor instance, so the note you just made was
 *  unlinkable until a reload. */
const CACHE_TTL_MS = 30_000;

/** One suggestible link target: the node's `label` (the string a `[[…]]` is
 *  resolved by — see `/api/nodes/resolve`) plus its type, shown as `detail`. */
export interface WikilinkCandidate {
  label: string;
  type: string;
}

/** Injectable candidate lookup — the real one hits the search API; tests pass a
 *  stub so the completion source can be exercised without a network or a view. */
export type WikilinkFetcher = (term: string) => Promise<WikilinkCandidate[]>;

/** Default fetcher: the studio's existing search endpoint. There is deliberately
 *  no dedicated "suggest" route — search already ranks nodes by relevance across
 *  lexical + semantic hits, which is exactly what a link picker wants.
 *
 *  `node.label` (not `node.title`) is what gets inserted: wikilink resolution is
 *  by label, so suggesting a title would produce links that resolve to nothing. */
async function searchNodes(term: string): Promise<WikilinkCandidate[]> {
  const res = await api.request<{ query: string; hits: SearchHit[] }>(
    "GET",
    `/api/search?q=${encodeURIComponent(term)}&limit=10`,
  );
  return res.hits
    .map((hit) => ({ label: hit.node.label, type: hit.node.type as string }))
    .filter((c) => c.label.trim().length > 0);
}

/** Replace the whole open wikilink with `[[label]]`, caret past the closer.
 *
 *  `start` is the document position of the opening `[[`, handed in by the
 *  completion source. It is NOT derivable from the `from` CodeMirror passes
 *  back: that one points at the start of the TERM, which the source advances
 *  past any whitespace the user left after the brackets — so `from - 2` lands
 *  one character INSIDE `[[` for `[[ Thr` and accepting leaves a stray `[`.
 *
 *  The other delicate part: `closeBrackets` (see `autopair.ts`) has almost
 *  always ALREADY inserted `]]` when the user typed `[[`, so the document reads
 *  `[[Thr|]]` at the moment a suggestion is accepted. Replacing only up to the
 *  caret would leave those brackets behind and yield `[[Label]]]]`. So the
 *  replacement range swallows up to two `]` sitting at the caret — absorbing
 *  0, 1 or 2 covers the closer-absent case (pasted text, a `]` deleted by hand)
 *  with the same code path. */
function applyWikilink(label: string, start: number) {
  return (view: EditorView, completion: Completion, _from: number, to: number) => {
    const doc = view.state.doc;
    let end = to;
    while (end < doc.length && end - to < 2 && doc.sliceString(end, end + 1) === "]") end++;
    view.dispatch({
      ...insertCompletionText(view.state, `[[${label}]]`, start, end),
      annotations: pickedCompletion.of(completion),
    });
  };
}

/** Everything the atlas overview already knows about, as link targets. Read
 *  straight from the react-query cache — no request, no loading state — so the
 *  picker has something to show the instant `[[` is typed. */
function cachedCandidates(): WikilinkCandidate[] {
  const overview = queryClient.getQueryData<GraphOverview>(keys.overview);
  if (!overview) return [];
  return [
    ...(overview.annotation_nodes ?? []),
    ...(overview.document_nodes ?? []),
    ...(overview.org_nodes ?? []),
    ...(overview.concept_nodes ?? []),
  ]
    .filter((n) => n?.label)
    .slice(0, 50)
    .map((n) => ({ label: n.label, type: String(n.type) }));
}

/** Dedupe and order the raw candidates. The key is label+type, not label alone:
 *  a document and a note may legitimately share a name, and keeping both (told
 *  apart by `detail`) is the whole reason `detail` carries the node type.
 *
 *  `start` (the position of the `[[`) rides into every option's `apply` — see
 *  `applyWikilink` for why it cannot be recomputed at accept time. */
function toOptions(candidates: WikilinkCandidate[], start: number): Completion[] {
  const seen = new Map<string, Completion>();
  for (const c of candidates) {
    const label = c.label.trim();
    const key = `${label} ${c.type}`;
    if (!label || seen.has(key)) continue;
    seen.set(key, {
      label,
      detail: c.type.replace(/_/g, " "),
      // A titled section reads as "you are picking a link", not as a code
      // completion that happened to appear.
      section: LINK_SECTION,
      apply: applyWikilink(label, start),
    });
  }
  return [...seen.values()].sort((a, b) => a.label.localeCompare(b.label));
}

/** The pure completion source, exported so it can be unit-tested against a bare
 *  `EditorState` + `CompletionContext` (no live `EditorView`, no network).
 *
 *  Cheapness comes from three things: an empty term bails out instead of firing
 *  a wildcard search; every term's promise is memoised per source instance, so
 *  backspacing over a prefix costs nothing; and CodeMirror's own
 *  `activateOnTypingDelay` already rate-limits keystrokes, so this adds no
 *  debounce of its own. */
export function wikilinkCompletionSource(
  fetchCandidates: WikilinkFetcher = searchNodes,
): CompletionSource {
  const cache = new Map<string, { at: number; candidates: Promise<WikilinkCandidate[]> }>();

  return async (context) => {
    const open = context.matchBefore(OPEN_WIKILINK);
    if (!open) return null;

    const raw = open.text.slice(2); // past the "[["
    const term = raw.trim();
    const from = open.from + 2 + (raw.length - raw.trimStart().length);

    // A bare "[[" opens the picker straight away rather than sitting blank until
    // the first keystroke — the point of a link picker is to be browsable when
    // you cannot remember the exact title. The candidates come from the atlas
    // overview ALREADY in the query cache, so this costs no request; typing then
    // takes over with real search results.
    if (!term) {
      const options = toOptions(cachedCandidates(), open.from);
      return options.length ? { from, options } : null;
    }

    let entry = cache.get(term);
    if (entry && Date.now() - entry.at > CACHE_TTL_MS) {
      cache.delete(term);
      entry = undefined;
    }
    if (!entry) {
      if (cache.size >= CACHE_LIMIT) cache.clear();
      entry = { at: Date.now(), candidates: fetchCandidates(term) };
      cache.set(term, entry);
      // A transient failure must not poison the term for the rest of the session.
      void entry.candidates.catch(() => cache.delete(term));
    }

    let candidates: WikilinkCandidate[];
    try {
      candidates = await entry.candidates;
    } catch {
      return null; // offline / 500 — silently no suggestions, never a thrown popup
    }
    if (context.aborted) return null;

    const options = toOptions(candidates, open.from);
    if (options.length === 0) return null;

    return {
      // `from` is the start of the TERM, not of the `[[`: CodeMirror filters and
      // highlights options against the text in [from, pos), which has to be the
      // bare target for "Thr" to match the label "Three laws". The `[[` itself
      // travels to `applyWikilink` as `open.from` above — deriving it back from
      // this number is what used to leave a stray bracket behind.
      from,
      options,
    };
  };
}

/** The suggestion popup, dressed to match the studio's popover surface. The
 *  autocomplete package's base theme ships a light-grey MONOSPACE list, which
 *  reads as a foreign control inside a prose editor. Everything here is driven
 *  by the shadcn tokens in `styles.css`, so a brand-theme switch carries.
 *
 *  This lives with the feature rather than in `theme.ts` because it styles a
 *  floating tooltip, not the document surface `theme.ts` keeps in sync with
 *  `.md-column`. */
const completionTheme = EditorView.theme({
  ".cm-tooltip.cm-tooltip-autocomplete": {
    background: "hsl(var(--popover))",
    border: "1px solid hsl(var(--border))",
    borderRadius: "var(--radius)",
    boxShadow: "0 8px 24px rgb(0 0 0 / 0.45)",
    overflow: "hidden",
  },
  ".cm-tooltip.cm-tooltip-autocomplete > ul": {
    fontFamily: "var(--font-sans)",
    fontSize: "0.8125rem",
  },
  ".cm-tooltip.cm-tooltip-autocomplete > ul > li": {
    alignItems: "baseline",
    color: "hsl(var(--popover-foreground))",
    display: "flex",
    gap: "0.75rem",
    padding: "0.25rem 0.625rem",
  },
  ".cm-tooltip.cm-tooltip-autocomplete > ul > li[aria-selected]": {
    background: "hsl(var(--accent))",
    color: "hsl(var(--accent-foreground))",
  },
  ".cm-completionLabel": { flex: "1 1 auto", overflow: "hidden", textOverflow: "ellipsis" },
  // The matched substring is underlined by default; a colour weight reads better
  // against a dark surface.
  ".cm-completionMatchedText": {
    color: "hsl(var(--primary))",
    fontWeight: "600",
    textDecoration: "none",
  },
  ".cm-completionDetail": {
    color: "hsl(var(--muted-foreground))",
    flex: "0 0 auto",
    fontSize: "0.6875rem",
    fontStyle: "normal",
  },
});

/** The extension to drop into the editor's config. Pair it with
 *  `...completionKeymap` in the host keymap — `defaultKeymap: false` hands
 *  keybinding ORDER to the caller, which matters because markdown's Enter
 *  (continue-list) would otherwise race the completion's Enter (accept). */
export function wikilinkComplete(): Extension {
  return [
    autocompletion({
      override: [wikilinkCompletionSource()],
      defaultKeymap: false,
      // No `type` is set on our options, so the icon column would only ever
      // render an empty gutter. Turning icons off keeps the popup tight.
      icons: false,
      // `acceptCompletion` REFUSES for the first `interactionDelay` ms (75 by
      // default) after the popup opens, and a refused Enter falls straight
      // through to `insertNewlineAndIndent` — which splits the half-typed link
      // into "[[Thr" / "]]" across two lines. That guard is meant for popups
      // that appear under a fast typist's fingers; ours cannot, because it only
      // opens after `activateOnTypingDelay` (100 ms of quiet) AND a search
      // round-trip. So the window buys nothing and only makes Enter a race.
      interactionDelay: 0,
    }),
    completionTheme,
  ];
}
